import { ArrowLeft } from '@dreamstack/icons'
import type { FullProjectImageMapFragment } from '@dreamstack/investors-graphql'
import { PropertyStatus } from '@dreamstack/web-graphql'
import type { PropertyPreview } from '@dreamstack/web/lib/prisma/properties'
import filter from 'lodash/filter'
import find from 'lodash/find'
import includes from 'lodash/includes'
import map from 'lodash/map'
import { useRouter } from 'next/router'
import type { FunctionComponent} from 'react';
import { useState } from 'react'
import tw from 'twin.macro'
import { useRefWidth } from '../../../feature-components/src/hooks/useRefWidth'
import { useGetPropertyUrl } from '../property/routing/useGetPropertyUrl'
import type { ImageMapArea, ImageMapImage } from './ImageMap';
import { ImageMap } from './ImageMap'

type ProjectImageMapArea = ImageMapArea & {
  targetImageIndex?: number
  targetPropertyName?: string
  targetProperty?: PropertyPreview
}

const BackButton = tw(
  ArrowLeft
)`absolute top-2 left-2 cursor-pointer text-black z-10`

const DEFAULT_HREF = '#'
const DEFAULT_WIDTH = 600

const FREE_PROPERTY_STATI = [PropertyStatus.FREE]

export const ProjectImageMap: FunctionComponent<React.PropsWithChildren<{
  projectImageMap: FullProjectImageMapFragment
  properties: PropertyPreview[]
}>> = ({ projectImageMap, properties }) => {
  const { ref, width } = useRefWidth({ defaultWidth: DEFAULT_WIDTH })

  const router = useRouter()
  const getPropertyUrl = useGetPropertyUrl()
  const [activeIndex, setActiveIndex] = useState(0)
  const images = map(
    projectImageMap?.imagesCollection?.items,
    (i) => i as ImageMapImage
  )
  const activeImage = images[activeIndex]
  const areas = map(
    projectImageMap.imageMapAreas as ProjectImageMapArea[],
    (area) => {
      const { targetPropertyName } = area
      const targetProperty = find(
        properties,
        (p) => p.name === targetPropertyName
      )
      const status = targetProperty?.status
      const propertyIsFree = includes(FREE_PROPERTY_STATI, status)
      const isClickableProperty = propertyIsFree
      const href =
        isClickableProperty && targetProperty?.id
          ? getPropertyUrl({
              id: targetProperty.id,
              city: targetProperty.city ?? undefined,
            })
          : DEFAULT_HREF

      const colorHex = !!targetPropertyName
        ? propertyIsFree
          ? '#508090'
          : '#991B1B'
        : undefined

      return {
        ...area,
        targetProperty,
        href,
        colorHex,
      }
    }
  )
  const activeAreas = filter(areas, (a) => a.imageIndex === activeIndex)

  const onClick = (area: ProjectImageMapArea) => {
    if (area.targetImageIndex) {
      setActiveIndex(area.targetImageIndex)
    }

    // This is necessary, because ImageMapper preventsDefaults when we pass onClick to the whole Map
    if (area.href && area.href !== DEFAULT_HREF) {
      router.push(area.href)
    }
  }
  return (
    <div tw="col-span-full flex flex-row justify-center" ref={ref}>
      <div tw="relative">
        {activeIndex !== 0 && <BackButton onClick={() => setActiveIndex(0)} />}
        <ImageMap
          image={activeImage}
          areas={activeAreas}
          onClick={onClick}
          width={width ?? DEFAULT_WIDTH}
        />
      </div>
    </div>
  )
}
